import React, { useState, useEffect } from "react";
import { Form, Modal, Button, Input, Select, DatePicker, notification } from "antd";
import { SaveFilled } from '@ant-design/icons';
import dayjs from "dayjs";

const { TextArea } = Input;

const TasksModal = ({
  isModalOpen,
  setIsModalOpen,
  editingTask,
  form,
  handleSave,
  taskStatus,
  comments,
  setComments,
  newComment,
  setNewComment,
  handleAddComment,
  isSubmitting,
  loading = false
}) => {
  const [api, contextHolder] = notification.useNotification();
  const selectedStatusId = Form.useWatch('status', form);

  // ✅ Verificar si la tarea está finalizada
  const isTaskFinished = editingTask?.status?.index === 3;

  // Efecto para manejar fecha de finalización automática
  useEffect(() => {
    const finishedStatus = taskStatus?.find(s => s.index === 3);
    
    if (selectedStatusId === finishedStatus?.id) {
      form.setFieldsValue({ 
        date_finished: dayjs()
      });
      
      // Auto-llenar fecha comprometida si está vacía
      const currentDateCommitted = form.getFieldValue('date_committed');
      if (!currentDateCommitted) {
        form.setFieldsValue({
          date_committed: dayjs()
        });
      }
    } else {
      form.setFieldsValue({ date_finished: null });
    }
  }, [selectedStatusId, form, taskStatus]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setComments([]);
    setNewComment('');
    form.resetFields();
  };

  return (
    <>
      {contextHolder}
      <Modal
        className="dependency-modal"
        title={<>Tareas para {editingTask?.issue?.type}</>}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={[
          <Button 
            key="cancel" 
            className="cancel-button" 
            onClick={handleCancel}
          >
            Cancelar
          </Button>,
          <Button 
            key="save" 
            className="save-type-button" 
            onClick={() => form.submit()} 
            icon={<SaveFilled />}
            disabled={isTaskFinished || loading}
          >
            Guardar
          </Button>
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            label="Nombre"
            name="name"
          >
            <Input.TextArea
              disabled
              style={{ resize: 'none', width: '100%' }} 
            />
          </Form.Item>
          <Form.Item
            label="Descripción"
            name="description"
          >
            <Input.TextArea 
              disabled
              style={{ resize: 'none', width: '100%', height: '90px' }} 
            />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              label="Estado"
              name="status"
              rules={[{ required: true, message: 'El estado es requerido' }]}
            >
              <Select disabled={isTaskFinished || loading}>
                {taskStatus?.map(status => (
                  <Select.Option key={status.index} value={status.id}>
                    {status.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <Form.Item 
              label="Responsable"
              name="responsible_area"
            >
              <Input disabled />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item 
              label="Fecha Comprometida" 
              name="date_committed"
              required={(() => {
                const initiatedStatus = taskStatus?.find(s => s.index === 2);
                return selectedStatusId === initiatedStatus?.id;
              })()}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const initiatedStatus = taskStatus?.find(s => s.index === 2);
                    const pendingStatus = taskStatus?.find(s => s.index === 1);
                    
                    // Estado "Pendiente" => NO obligatorio
                    if (statusId === pendingStatus?.id) {
                      return Promise.resolve();
                    }
                    
                    // Estado "Iniciado" => Obligatorio
                    if (statusId === initiatedStatus?.id && !value) {
                      return Promise.reject(new Error('La fecha comprometida es obligatoria'));
                    }

                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                inputReadOnly
                disabled={(() => {
                  const finishedStatus = taskStatus?.find(s => s.index === 3);
                  
                  return (
                    isTaskFinished ||
                    loading ||
                    selectedStatusId === finishedStatus?.id
                  );
                })()}
                disabledDate={(current) => {
                  // Permitir HOY y futuro
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>

            <Form.Item 
              label="Fecha Finalización" 
              name="date_finished"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const statusId = getFieldValue('status');
                    const finishedStatus = taskStatus?.find(s => s.index === 3);
                    
                    // ✅ Comparar con el ID del estado "Finalizado"
                    if (statusId === finishedStatus?.id && !value) {
                      return Promise.reject(new Error('La fecha de finalización es requerida cuando el estado es "Finalizada"'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <DatePicker 
                style={{ width: '100%' }} 
                format="DD/MM/YYYY"
                inputReadOnly
                disabled={(() => {
                  const finishedStatus = taskStatus?.find(s => s.index === 3);
                  const currentDateCommitted = form.getFieldValue('date_committed');
                  
                  return (
                    isTaskFinished ||
                    loading ||
                    (selectedStatusId === finishedStatus?.id && currentDateCommitted)
                  );
                })()}
                disabledDate={(current) => {
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>
          </div>

          <Form.Item label="Comentarios" style={{ marginBottom: 24 }}>
            <div className="comments-container">
              <div className={`comments-input-area ${comments.length > 0 ? 'with-border' : ''}`}>
                <TextArea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escribe tu comentario (máx. 300 caracteres)"
                  autoSize={{ minRows: 3, maxRows: 3 }}
                  maxLength={300}
                  className="comments-textarea"
                  disabled={isTaskFinished || loading}
                />
                <div className="comments-actions">
                  <span className="comments-counter">
                    {newComment.length}/300
                  </span>
                  <Button 
                    type="primary"
                    size="small"
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || isSubmitting || isTaskFinished || loading}
                    loading={isSubmitting}
                    className="comments-button"
                  >
                    Comentar
                  </Button>
                </div>
              </div>
              
              {comments.length > 0 ? (
                <div className="comments-list">
                  {comments.map((comment) => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-header">
                        <span className="comment-user">{comment.user}</span>
                        <span className="comment-date">
                          {dayjs(comment.created).format('DD/MM/YYYY HH:mm')}
                        </span>
                      </div>
                      <div className="comment-text">
                        {comment.text}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="comments-empty">
                  No hay comentarios todavía
                </div>
              )}
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TasksModal;